/**
 * Auto-reload for a lazy page whose chunk is gone after a deploy. A counter in
 * sessionStorage caps it at MAX_RELOADS within RELOAD_WINDOW_MS, so a truly
 * missing chunk can't loop, and the URL stays clean.
 */

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
];

const STORAGE_KEY = "customer-portal:chunk-reload";
const MAX_RELOADS = 2;
const RELOAD_WINDOW_MS = 30_000;

interface ReloadAttempts {
  count: number;
  since: number;
}

/** True when `error` is a failed lazy-chunk load. */
export function isDynamicImportError(error: unknown): boolean {
  return (
    error instanceof Error &&
    DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(error.message))
  );
}

/** The attempts after one more reload, or `null` at the cap. Stale attempts reset. */
export function nextReloadAttempts(
  previous: ReloadAttempts | null,
  now: number
): ReloadAttempts | null {
  const current =
    previous && now - previous.since < RELOAD_WINDOW_MS ? previous : { count: 0, since: now };
  return current.count < MAX_RELOADS ? { ...current, count: current.count + 1 } : null;
}

/** Records a reload attempt and says whether one is allowed. False if storage is unavailable. */
export function claimReload(storage: Storage = window.sessionStorage, now = Date.now()): boolean {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const next = nextReloadAttempts(raw ? (JSON.parse(raw) as ReloadAttempts) : null, now);
    if (!next) return false;
    storage.setItem(STORAGE_KEY, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
}
